import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import VirtualCard from "../components/VirtualCard";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { api, formatUSD, formatCcy } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, ArrowRight, CreditCard, ClockCounterClockwise, Receipt, PencilSimple, Trash, Bank, Heart, Share, Copy, ShieldCheck, Warning as WarningIcon } from "@phosphor-icons/react";
import LoadFundsButton from "../components/LoadFundsButton";
import SupportContact from "../components/SupportContact";
import { IssuanceSourceBadge } from "../components/IssuanceSourceBadge";
import { CardLifecycleStrip, MoneyFlowStrip } from "../components/CardLifecycleStrip";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user, isImpersonating } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [cards, setCards] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [seedAmount, setSeedAmount] = useState(null);
  const [pendingLoadCard, setPendingLoadCard] = useState(null);
  const [banks, setBanks] = useState([]);
  const [prefillBank, setPrefillBank] = useState(null);

  const load = async () => {
    const r = await api.get("/cards");
    setCards(r.data);
    try {
      const b = await api.get("/bank-verification/mine");
      setBanks(b.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  // Auto-open Issue card flow when ?issue_amount=NNN is present (e.g. coming from landing CTA).
  // Also handles `?reissue_from=<card_id>` for the "Re-issue this card to a new recipient"
  // flow on the card detail page — fetches the source card to seed the original amount.
  useEffect(() => {
    if (isImpersonating) return;
    const params = new URLSearchParams(window.location.search);
    const amt = Number(params.get("issue_amount"));
    const reissueFrom = params.get("reissue_from");
    if (amt > 0) {
      setSeedAmount(amt);
      setIssueOpen(true);
      params.delete("issue_amount");
      const clean = params.toString();
      window.history.replaceState(null, "", "/dashboard" + (clean ? `?${clean}` : ""));
    } else if (reissueFrom) {
      // Pre-fetch the source card and seed the dialog with the same load amount.
      // We intentionally LEAVE receiver_* blank — re-issuing to the *same* person
      // is rare; the natural pattern is "send another card, different recipient".
      api.get(`/cards/${reissueFrom}`)
        .then((r) => {
          const src = r?.data || {};
          const original = Number(src?.quote?.principal_vnd || src?.source_amount || 0);
          if (original > 0) setSeedAmount(original);
          setIssueOpen(true);
        })
        .catch(() => { setIssueOpen(true); })  // open empty dialog rather than swallow
        .finally(() => {
          params.delete("reissue_from");
          const clean = params.toString();
          window.history.replaceState(null, "", "/dashboard" + (clean ? `?${clean}` : ""));
        });
    }
  }, [isImpersonating]);

  const stats = {
    total: cards.length,
    active: cards.filter((c) => c.status === "active").length,
    totalUsd: cards.filter((c) => c.status === "active").reduce((s, c) => s + (c.balance_vnd ?? c.quote?.principal_vnd ?? 0), 0),
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">{t("dash.eyebrow")}</p>
            <h1 className="serif text-5xl mt-2">{t("dash.greeting", { name: user?.full_name?.split(" ")[0] || "" })}</h1>
            <p className="text-[#4A5C50] mt-2">{t("dash.subtitle")}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              className="h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setIssueOpen(true)}
              disabled={isImpersonating}
              title={isImpersonating ? "View-as mode is read-only" : undefined}
              data-testid="issue-new-card-btn"
            >
              <Plus size={18} className="mr-1" /> {t("dash.action.issue_card")}
            </Button>
            <Link to="/direct-deposits" data-testid="dashboard-direct-deposits-link">
              <Button
                type="button"
                className="h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-6"
                disabled={isImpersonating}
              >
                <Bank size={18} weight="duotone" className="mr-2" /> {t("dash.action.direct_deposit")}
              </Button>
            </Link>
            <Link to="/transactions" data-testid="dashboard-transactions-link">
              <Button
                type="button"
                variant="outline"
                className="h-12 rounded-xl border-[#184A30]/40 text-[#184A30] hover:bg-[#184A30]/5 px-6"
              >
                Transactions
              </Button>
            </Link>
          </div>
          <SupportContact />
          <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
            <IssueCardDialog
              open={issueOpen}
              seedAmount={seedAmount}
              onIssued={(card) => {
                setIssueOpen(false);
                setSeedAmount(null);
                setPendingLoadCard(card);
              }}
              onClose={() => { setIssueOpen(false); setSeedAmount(null); load(); }}
            />
          </Dialog>
          {pendingLoadCard && (
            <LoadFundsButton
              key={pendingLoadCard.id + (prefillBank?.id || "")}
              card={pendingLoadCard}
              hideTrigger
              defaultOpen
              prefillBank={prefillBank}
              prefillAmount={pendingLoadCard?.quote?.principal_vnd ?? null}
              onDone={() => { setPendingLoadCard(null); setPrefillBank(null); load(); }}
            />
          )}
        </div>

        {/* BankVerificationStatusBanner removed per user request — verification still happens inline during the first load-funds step */}

        <LinkedBanksSection
          banks={banks}
          cards={cards}
          onUse={async (bank) => {
            try {
              const r = await api.post(`/bank-verification/${bank.id}/use`);
              const activeCards = cards.filter((c) => c.status === "active" || c.status === "pending_payment");
              if (activeCards.length === 0) {
                toast.error("Issue a card first — no card to fund.");
                return;
              }
              const card = activeCards[0];
              setPrefillBank(r.data);
              setPendingLoadCard(card);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not open bank");
            }
          }}
          onUnlink={async (bank) => {
            try {
              await api.delete(`/bank-verification/${bank.id}`);
              toast.success("Bank unlinked");
              load();
            } catch (e) {
              toast.error("Could not unlink");
            }
          }}
        />

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <Stat icon={<CreditCard size={24} weight="duotone" />} label="Cards issued" value={stats.total} />
          <Stat icon={<ClockCounterClockwise size={24} weight="duotone" />} label="Active" value={stats.active} />
          <Stat icon={<Receipt size={24} weight="duotone" />} label="Total loaded" value={formatUSD(stats.totalUsd)} />
        </div>

        <RecentActivityStrip />
        <ThankYouFeed />
        <MaintenanceBanner />
        {/* ReferralLinkPanel removed per user request */}

        <div className="mb-6">
          <MoneyFlowStrip />
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8" data-testid="cards-list">
          <h2 className="serif text-3xl mb-6">Recent cards</h2>
          {cards.length === 0 && (
            <p className="text-[#4A5C50] text-sm">No cards yet. Issue your first one above.</p>
          )}
          <div className="divide-y divide-[#E5E0D8]">
            {cards.map((c) => (
              <CardRow key={c.id} card={c} onChanged={load} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 card-lift">
    <div className="text-[#184A30]">{icon}</div>
    <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-4">{label}</p>
    <p className="serif text-4xl mt-1">{value}</p>
  </div>
);
const StatusChip = ({ status }) => {
  const map = {
    active: { c: "bg-[#184A30]/10 text-[#184A30]", t: "Active" },
    pending_payment: { c: "bg-[#D96C4A]/15 text-[#B04E2F]", t: "Pending" },
    expired: { c: "bg-neutral-200 text-neutral-700", t: "Expired" },
  };
  const s = map[status] || { c: "bg-neutral-200 text-neutral-700", t: status };
  return <span className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full ${s.c}`}>{s.t}</span>;
};

// Visible proof to the sender that the receiver was auto-notified after
// activation (closes the "did they actually get it?" anxiety loop). Surfaces
// the most recent successful email/SMS delivery row from the backend's bulk
// fetch in /api/cards. Cards that haven't been delivered yet (pending) show
// nothing — pill only appears once delivery has been attempted.
function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function DeliveryStatusPill({ card }) {
  // Only meaningful for activated cards — pending cards haven't been
  // delivered yet by definition.
  if (card.status !== "active") return null;
  const emailedAt = card.notified_email_at;
  const smsedAt = card.notified_sms_at;
  const fallbackAt = card.fallback_fired_at;

  const parts = [];
  if (emailedAt) parts.push(`email · ${timeAgo(emailedAt)}`);
  if (smsedAt) parts.push(`SMS · ${timeAgo(smsedAt)}`);

  return (
    <>
      {parts.length > 0 && (
        <span
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
          title={`Receiver notified — ${parts.join(" + ")}`}
          data-testid={`delivery-pill-${card.id}`}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Notified · {parts.join(" + ")}
        </span>
      )}
      {fallbackAt && (
        <span
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300"
          title={`Image attachment rejected by carrier — receiver got card details as text-only SMS. ${timeAgo(fallbackAt)}. Re-send manually once PUBLIC_BASE_URL is on production domain to deliver the rich card image.`}
          data-testid={`fallback-pill-${card.id}`}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
          Sent as text · no image
        </span>
      )}
    </>
  );
}

function FundingSourceLine({ card }) {
  const topups = Array.isArray(card.topups) ? card.topups : [];
  if (topups.length === 0) return null;
  const latest = topups[topups.length - 1] || topups[0];
  const label = latest?.source_label;
  if (!label) return null;
  const isBank = (latest?.payment_method || "").toLowerCase().includes("bank");
  return (
    <p
      className="text-[11px] mt-1 inline-flex items-center gap-1.5 text-[#4A5C50]"
      title={`Last loaded from ${label}`}
      data-testid={`funding-source-${card.id}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${isBank ? "bg-[#184A30]" : "bg-[#CBA35C]"}`} />
      <span className="uppercase tracking-[0.15em] text-[10px]">
        {isBank ? "Bank" : "Debit"}
      </span>
      <span className="mono text-[10px] text-[#112318]">{label.replace(/^(Bank|Debit card)\s*/i, "")}</span>
      {topups.length > 1 && (
        <span className="text-[10px] opacity-50">+{topups.length - 1} prior</span>
      )}
    </p>
  );
}

function CardRow({ card, onChanged }) {
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [form, setForm] = useState({
    receiver_name: card.receiver_name || "",
    receiver_email: card.receiver_email || "",
    receiver_phone: card.receiver_phone || "",
    note: card.note || "",
  });
  const [busy, setBusy] = useState(false);

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/cards/${card.id}`, form);
      toast.success("Receiver updated");
      setEditOpen(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success("Card removed");
      setDelOpen(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-4 py-4 px-2 -mx-2 rounded-lg hover:bg-[#FBF9F6] transition" data-testid={`card-row-${card.id}`}>
      <Link to={`/cards/${card.id}`} className="flex items-center gap-4 flex-1 min-w-0">
        {card.inline_image_url ? (
          // Show the EXACT rendered card that gets emailed/texted to the
          // receiver — closes the "did I send a blank card?" anxiety loop.
          // Aspect ratio matches the card render (1410:888 ≈ 16:10), shrunk
          // to a 96×60 thumbnail. Falls back to the icon if the URL is null
          // (pending_payment cards, or backend hasn't minted yet).
          <img
            src={card.inline_image_url}
            alt={`Card for ${card.receiver_name}`}
            className="w-24 h-[60px] rounded-md object-cover shrink-0 shadow-sm ring-1 ring-black/5"
            loading="lazy"
            data-testid={`card-thumb-${card.id}`}
          />
        ) : (
          <div className="w-24 h-[60px] rounded-md bg-[#184A30]/10 flex items-center justify-center text-[#184A30] shrink-0">
            <CreditCard size={24} weight="duotone" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{card.receiver_name}</p>
          <p className="text-xs text-[#4A5C50] mono truncate">{card.receiver_email}</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <IssuanceSourceBadge provider={card.provider} network={card.network} size="xs" />
            <DeliveryStatusPill card={card} />
          </div>
          <FundingSourceLine card={card} />
        </div>
        <div className="text-right hidden sm:block">
          <p className="mono text-sm">{formatUSD(card.source_amount)}</p>
          <p className="text-xs text-[#4A5C50]">loaded</p>
        </div>
        <StatusChip status={card.status} />
      </Link>
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="mr-1" data-testid={`row-load-funds-${card.id}`}>
          <LoadFundsButton card={card} onDone={onChanged} size="sm" />
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-[#4A5C50] hover:text-[#184A30] h-9 w-9 p-0"
          onClick={() => setEditOpen(true)}
          aria-label="Edit receiver"
          data-testid={`edit-card-${card.id}`}
        >
          <PencilSimple size={16} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-[#4A5C50] hover:text-[#D96C4A] h-9 w-9 p-0"
          onClick={() => setDelOpen(true)}
          aria-label="Delete card"
          data-testid={`delete-card-${card.id}`}
        >
          <Trash size={16} />
        </Button>
        <Link to={`/cards/${card.id}`} className="text-[#4A5C50] p-2" aria-label="Open card">
          <ArrowRight size={16} />
        </Link>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8]" data-testid="edit-card-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Edit receiver</DialogTitle>
            <DialogDescription className="text-[#4A5C50]">Only receiver contact details can be edited. Card number and balance stay locked.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-5 py-2">
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver name</Label>
              <input className="underline-input" value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} required data-testid="edit-receiver-name" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver email</Label>
              <input type="email" className="underline-input mono" value={form.receiver_email} onChange={(e) => setForm({ ...form, receiver_email: e.target.value })} required data-testid="edit-receiver-email" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver phone</Label>
              <input className="underline-input mono" value={form.receiver_phone} onChange={(e) => setForm({ ...form, receiver_phone: e.target.value })} data-testid="edit-receiver-phone" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Note</Label>
              <input className="underline-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="edit-note" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} data-testid="edit-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="edit-save-btn">
                {busy ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8]" data-testid="delete-card-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Remove card?</DialogTitle>
            <DialogDescription className="text-[#4A5C50]">
              This removes the card for <span className="font-medium text-[#112318]">{card.receiver_name}</span> from your dashboard. It cannot be undone.
              {card.status === "active" && " Any remaining balance will not be refunded automatically — please use the Refund notice form first if needed."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)} data-testid="delete-cancel-btn">Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={busy}
              className="rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white"
              data-testid="delete-confirm-btn"
            >
              {busy ? "Removing…" : "Yes, remove card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IssueCardDialog({ open, onClose, seedAmount, onIssued }) {
  const [form, setForm] = useState({
    receiver_name: "", receiver_email: "", receiver_phone: "",
    source_currency: "USD",
    source_amount: seedAmount && Number(seedAmount) > 0 ? Number(seedAmount) : 100,
    note: "",
    notify_on_delivery: true,
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [notifiedCount, setNotifiedCount] = useState(null);
  const [avgOpenSeconds, setAvgOpenSeconds] = useState(null);
  const [recentRecipients, setRecentRecipients] = useState([]);

  // Pull a small social-proof counter when the dialog opens — how many
  // senders we've notified in the last 7 days when their recipient
  // opened the card, plus the average open-time. Fail-silent.
  useEffect(() => {
    if (!open) return;
    api.get("/stats/delivery-notifications")
      .then((r) => {
        setNotifiedCount(r?.data?.notified_7d ?? null);
        setAvgOpenSeconds(r?.data?.avg_open_seconds ?? null);
      })
      .catch(() => { setNotifiedCount(null); setAvgOpenSeconds(null); });
    // Pull this sender's past recipients so we can render the chip rail.
    // Empty array on failure → rail stays hidden (no embarrassing blank state).
    api.get("/recipients/recent")
      .then((r) => setRecentRecipients(r?.data?.recipients || []))
      .catch(() => setRecentRecipients([]));
  }, [open]);

  // Fill the receiver fields from a chip tap. Doesn't touch amount or
  // delivery preferences — sender still decides each card's payload.
  const applyRecipient = (rcp) => {
    setForm((f) => ({
      ...f,
      receiver_name: rcp.name || "",
      receiver_email: rcp.email || "",
      receiver_phone: rcp.phone || "",
    }));
  };

  // Humanize seconds → "4h 12m" / "12m" / "2d 3h". Returns null for
  // suspiciously high values (> 30d) since they're almost certainly
  // backfilled data, not real recipient open behavior.
  const formatOpenTime = (secs) => {
    if (secs == null || !Number.isFinite(secs) || secs < 0) return null;
    if (secs > 30 * 86400) return null; // skew protection
    if (secs < 60) return "<1m";
    if (secs < 3600) return `${Math.round(secs / 60)}m`;
    if (secs < 86400) {
      const h = Math.floor(secs / 3600);
      const m = Math.round((secs % 3600) / 60);
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    const d = Math.floor(secs / 86400);
    const h = Math.round((secs % 86400) / 3600);
    return h > 0 ? `${d}d ${h}h` : `${d}d`;
  };

  const fetchQuote = async () => {
    const amt = Number(form.source_amount);
    if (!amt || amt <= 0) { setQuote(null); return; }
    try {
      setQuoteError("");
      const r = await api.post("/cards/quote", {
        source_currency: form.source_currency, source_amount: amt,
      });
      setQuote(r.data);
    } catch (err) {
      setQuote(null);
      setQuoteError(err?.response?.data?.detail || "Unable to compute quote");
    }
  };
  useEffect(() => { if (open) fetchQuote(); /* eslint-disable-next-line */ }, [form.source_amount, form.source_currency, open]);

  // Re-apply seed amount on every fresh open (handles the case where seedAmount arrives after mount).
  useEffect(() => {
    if (open && seedAmount && Number(seedAmount) > 0) {
      setForm((f) => ({ ...f, source_amount: Number(seedAmount) }));
    }
  }, [open, seedAmount]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.receiver_email);
  const amtOk = Number(form.source_amount) > 0;
  const nameOk = form.receiver_name.trim().length >= 2;
  const phoneDigits = (form.receiver_phone || "").replace(/\D/g, "");
  const phoneOk = phoneDigits.length >= 7;

  const reason = loading ? "Creating card…"
    : !nameOk ? "Enter receiver name"
    : !emailOk ? "Enter a valid receiver email"
    : !phoneOk ? "Enter a valid receiver phone"
    : !amtOk ? "Enter an amount greater than 0"
    : !quote ? (quoteError || "Calculating quote…")
    : null;

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/cards/issue", {
        ...form,
        source_amount: Number(form.source_amount),
        origin_url: window.location.origin,
      });
      // New flow: close this dialog and open Load Funds for the fresh pending card
      // so the customer can fund it directly via Bank / Debit into the holding account.
      toast.success("Card created. Now load funds to activate it.");
      if (onIssued) {
        onIssued({
          id: r.data.card_id,
          status: "pending_payment",
          balance_vnd: 0,
          source_amount: Number(form.source_amount),
          source_currency: form.source_currency,
          receiver_name: form.receiver_name,
          receiver_email: form.receiver_email,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to issue card");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl bg-[#FBF9F6] border-[#E5E0D8]">
      <DialogHeader>
        <DialogTitle className="serif text-3xl">Issue a virtual card</DialogTitle>
        <DialogDescription className="text-[#4A5C50]">Enter receiver details and the amount. We'll show the full quote before payment.</DialogDescription>
      </DialogHeader>
      <div className="grid md:grid-cols-2 gap-6 py-2">
        <div className="space-y-5">
          {/* Saved recipients — horizontally scrollable chip rail. Only
              renders when the sender has at least one past recipient,
              so first-time senders never see an empty/awkward state. */}
          {recentRecipients.length > 0 && (
            <div data-testid="saved-recipients-rail">
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50] flex items-center gap-2">
                <Heart size={11} weight="fill" className="text-[#D96C4A]" />
                Saved recipients · tap to fill
              </Label>
              <div
                className="mt-2 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1.5 snap-x"
                style={{ scrollbarWidth: "none" }}
              >
                {recentRecipients.map((r) => {
                  const initial = (r.name || r.email || "?").trim().charAt(0).toUpperCase() || "?";
                  const isActive = (
                    (r.name && r.name === form.receiver_name) ||
                    (r.email && r.email.toLowerCase() === form.receiver_email.toLowerCase())
                  );
                  return (
                    <button
                      key={`${r.email || ""}|${r.phone || ""}|${r.name}`}
                      type="button"
                      onClick={() => applyRecipient(r)}
                      className={`shrink-0 snap-start flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                        isActive
                          ? "border-[#184A30] bg-[#184A30]/10 text-[#184A30]"
                          : "border-[#E5E0D8] bg-white text-[#112318] hover:border-[#184A30]/40"
                      }`}
                      data-testid={`saved-recipient-chip-${(r.email || r.phone || r.name).toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 32)}`}
                      title={[r.name, r.email, r.phone].filter(Boolean).join(" · ")}
                    >
                      <span className="w-6 h-6 rounded-full bg-[#FBF9F6] border border-[#E5E0D8] flex items-center justify-center text-[10px] serif text-[#4A5C50]">
                        {initial}
                      </span>
                      <span className="font-medium truncate max-w-[140px]">{r.name || r.email || r.phone}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver name</Label>
            <input className="underline-input" value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} data-testid="issue-receiver-name" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver email</Label>
            <input type="email" className="underline-input mono" value={form.receiver_email} onChange={(e) => setForm({ ...form, receiver_email: e.target.value })} data-testid="issue-receiver-email" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Receiver phone</Label>
            <input
              type="tel"
              className="underline-input mono"
              value={form.receiver_phone}
              onChange={(e) => setForm({ ...form, receiver_phone: e.target.value })}
              placeholder="+1 (555) 123-4567"
              required
              data-testid="issue-receiver-phone"
            />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Amount (USD)</Label>
            <input type="number" min="1" step="0.01" className="underline-input mono text-2xl" value={form.source_amount} onChange={(e) => setForm({ ...form, source_amount: e.target.value })} data-testid="issue-amount" />
          </div>
          <label className="flex items-start gap-3 text-sm text-[#112318] cursor-pointer pt-2" data-testid="issue-notify-on-delivery-row">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[#184A30]"
              checked={form.notify_on_delivery}
              onChange={(e) => setForm({ ...form, notify_on_delivery: e.target.checked })}
              data-testid="issue-notify-on-delivery"
            />
            <span>
              Email me when my recipient opens this card
              <span className="block text-[12px] text-[#4A5C50] mt-0.5">
                One-time confirmation the moment they first scan the QR. You can change this later from the card's details.
              </span>
              {/* Social-proof counter — only shows when we have enough
                  weekly volume to feel real (≥ 3). Below that we'd be
                  showing a humble brag with low conviction, so we hide. */}
              {notifiedCount !== null && notifiedCount >= 3 && (
                <span
                  className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5"
                  data-testid="issue-notif-social-proof"
                >
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#184A30] bg-[#184A30]/10 rounded-full px-2 py-0.5">
                    <Heart size={11} weight="fill" className="text-[#D96C4A]" />
                    {notifiedCount.toLocaleString()} senders notified this week
                  </span>
                  {formatOpenTime(avgOpenSeconds) && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-[#4A5C50]"
                      data-testid="issue-avg-open-time"
                    >
                      <ClockCounterClockwise size={11} weight="fill" className="text-[#184A30]" />
                      Avg time to open: <span className="mono text-[#112318]">{formatOpenTime(avgOpenSeconds)}</span>
                    </span>
                  )}
                </span>
              )}
            </span>
          </label>
        </div>
        <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-6 relative overflow-hidden">
          <p className="text-[10px] tracking-[0.3em] opacity-60">QUOTE</p>
          <p className="serif text-4xl mt-2" data-testid="quote-principal-vnd">
            {quote ? formatUSD(quote.principal_vnd) : "—"}
          </p>
          <p className="text-xs opacity-70">credited to the card</p>
          <div className="mt-6 space-y-2 text-sm">
            <QRow k={`Service fee (${quote?.service_fee_percent || 3.5}%)`} v={quote ? formatUSD(quote.service_fee_source) : "—"} />
            <QRow k="Gateway cost" v={quote ? formatUSD(quote.gateway_fee_source) : "—"} />
            <div className="h-px bg-white/10 my-2" />
            <QRow k="Total charge" v={quote ? formatUSD(quote.total_source) : "—"} emphasis />
          </div>
          {quoteError && <p className="mt-4 text-xs text-[#D96C4A]">{quoteError}</p>}
        </div>
      </div>
      {/* Delivery channel preview — surfaces the truth before the sender
          pays. For US/CA receivers, backend SMS is blocked at carriers
          while TFV/10DLC is pending; we'll auto-email instead. For
          international, backend SMS goes through. This eliminates the
          "I issued and texted but receiver got nothing" frustration. */}
      {(() => {
        const phoneNorm = (form.receiver_phone || "").replace(/[^\d+]/g, "");
        if (!phoneNorm || phoneNorm.length < 7) return null;
        const isUsCa = !phoneNorm.startsWith("+") || phoneNorm.startsWith("+1");
        const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.receiver_email);
        if (isUsCa) {
          return (
            <div
              className="mt-2 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-3 text-sm text-[#8C6B21] flex items-start gap-2"
              data-testid="issue-delivery-preview-us"
            >
              <span className="text-base leading-none mt-0.5">📧</span>
              <div className="flex-1">
                <p className="font-semibold">Will be delivered by email</p>
                <p className="text-xs leading-relaxed mt-0.5">
                  US carriers currently block backend SMS from our toll-free number ({" "}
                  <a
                    href="/admin/twilio-10dlc-guide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    we're switching to 10DLC
                  </a>
                  ). For now, the card will land in {hasEmail ? form.receiver_email : "their email inbox"} as a PNG attachment.
                  Once the card is active you can also text it to them from your iPhone (one tap → opens Messages).
                </p>
              </div>
            </div>
          );
        }
        return (
          <div
            className="mt-2 rounded-2xl border border-[#184A30]/20 bg-[#184A30]/5 p-3 text-sm text-[#184A30] flex items-start gap-2"
            data-testid="issue-delivery-preview-intl"
          >
            <span className="text-base leading-none mt-0.5">📱</span>
            <div className="flex-1">
              <p className="font-semibold">Will be delivered by SMS + email</p>
              <p className="text-xs leading-relaxed mt-0.5">
                Backend SMS via Twilio works internationally — {form.receiver_phone} will receive a text with the
                card number, expiry, CVV, and a link to view the rendered card.
                {hasEmail ? <> A copy will also be emailed to <span className="mono">{form.receiver_email}</span>.</> : null}
              </p>
            </div>
          </div>
        );
      })()}
      <IssueTrustPill />
      <DialogFooter className="flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {checkoutUrl ? (
          <a
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto text-center rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-5 py-2.5 font-medium"
            data-testid="open-stripe-link"
          >
            Continue to secure Stripe checkout →
          </a>
        ) : (
          <>
            {reason && <p className="text-xs text-[#D96C4A] sm:mr-auto" data-testid="pay-disabled-reason">{reason}</p>}
            <Button variant="ghost" onClick={onClose} data-testid="issue-cancel-btn">Cancel</Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!!reason}
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white disabled:opacity-50"
              data-testid="issue-pay-btn"
            >
              {loading ? "Creating card…" : `Issue & load ${quote ? formatUSD(quote.total_source) : ""}`}
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
const QRow = ({ k, v, emphasis }) => (
  <div className={`flex justify-between ${emphasis ? "text-[#D96C4A] font-semibold" : "opacity-90"}`}>
    <span>{k}</span><span className="mono">{v}</span>
  </div>
);

function BankVerificationStatusBanner({ banks, onVerifyClick }) {
  const verified = (banks || []).filter((b) => b.status === "verified");
  const pending = (banks || []).filter((b) => b.status === "pending_verify");

  // Case 1: at least one verified bank → quiet green confirmation
  if (verified.length > 0) {
    return (
      <div
        className="mb-6 rounded-xl border border-[#184A30]/20 bg-[#184A30]/5 p-3 px-4 flex items-center gap-3 text-sm text-[#184A30]"
        data-testid="bank-status-verified"
      >
        <span className="inline-flex w-6 h-6 rounded-full bg-[#184A30] text-white items-center justify-center text-xs">✓</span>
        <span>
          Bank ready to fund — {verified.length} verified account{verified.length === 1 ? "" : "s"} on file
        </span>
      </div>
    );
  }

  // Case 2: only pending banks → amber "almost there"
  if (pending.length > 0) {
    return (
      <div
        className="mb-8 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5"
        data-testid="bank-status-pending"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8C6B21]">Almost ready</p>
            <p className="serif text-xl text-[#112318] mt-1">Confirm your micro-deposits</p>
            <p className="text-xs text-[#4A5C50] mt-2 leading-relaxed max-w-xl">
              We sent two small deposits to your bank account ({pending.length} pending). Once they post (1-3 business days),
              come back and confirm the amounts to unlock instant card funding.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Case 3: no banks at all → big yellow CTA
  return (
    <div
      className="mb-8 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-6"
      data-testid="bank-status-empty"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#8C6B21]">Bank not linked</p>
          <p className="serif text-2xl text-[#112318] mt-1">Verify your bank to load funds</p>
          <p className="text-xs text-[#4A5C50] mt-2 leading-relaxed">
            Zcardon funds virtual cards via secure ACH bank transfer. We&apos;ll send two micro-deposits to your bank
            account; you confirm the amounts to verify ownership. One-time setup, then load any card in seconds.
          </p>
          <ul className="mt-3 text-xs text-[#4A5C50] space-y-1">
            <li>• No processing fees on bank-funded loads</li>
            <li>• Funds credit the card the moment your bank settles</li>
            <li>• Unlimited reuse across every card you issue</li>
          </ul>
        </div>
        <Button
          type="button"
          onClick={onVerifyClick}
          className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white px-5"
          data-testid="bank-status-verify-btn"
        >
          <Plus size={14} className="mr-1.5" /> Link a bank
        </Button>
      </div>
    </div>
  );
}


function LinkedBanksSection({ banks, cards, onUse, onUnlink }) {
  if (!banks || banks.length === 0) return null;
  const verified = banks.filter((b) => b.status === "verified");
  const pending = banks.filter((b) => b.status === "pending_verify");
  if (verified.length === 0 && pending.length === 0) return null;
  const hasCard = cards.some((c) => c.status === "active" || c.status === "pending_payment");
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8 mb-10" data-testid="linked-banks">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Faster funding</p>
          <h2 className="serif text-3xl mt-1">Linked bank accounts</h2>
        </div>
        <p className="text-xs text-[#4A5C50]">{verified.length} verified · {pending.length} pending</p>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {verified.map((b) => (
          <div key={b.id} className="border border-[#E5E0D8] rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`linked-bank-${b.id}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{b.holder_name}</p>
              <p className="mono text-xs text-[#4A5C50] mt-0.5">Routing {b.routing_number} · {b.account_masked}</p>
              <p className="text-[11px] text-[#4A5C50] mt-1">Verified {b.verified_at ? new Date(b.verified_at).toLocaleDateString() : "—"}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                size="sm"
                className="rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white disabled:opacity-50"
                onClick={() => onUse(b)}
                disabled={!hasCard}
                title={hasCard ? "Load a card with this bank" : "Issue a card first"}
                data-testid={`linked-bank-use-${b.id}`}
              >
                Use to load
              </Button>
              <button
                type="button"
                onClick={() => onUnlink(b)}
                className="text-[11px] text-[#4A5C50] hover:text-[#D96C4A] transition"
                data-testid={`linked-bank-unlink-${b.id}`}
              >
                Unlink
              </button>
            </div>
          </div>
        ))}
        {pending.map((b) => (
          <div key={b.id} className="border border-dashed border-[#CBA35C]/50 rounded-xl p-4 flex items-center justify-between gap-3 bg-[#CBA35C]/5" data-testid={`linked-bank-${b.id}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{b.holder_name}</p>
              <p className="mono text-xs text-[#4A5C50] mt-0.5">Routing {b.routing_number} · {b.account_masked}</p>
              <p className="text-[11px] text-[#8C6B21] mt-1">Awaiting micro-deposit confirmation</p>
            </div>
            <button
              type="button"
              onClick={() => onUnlink(b)}
              className="text-[11px] text-[#4A5C50] hover:text-[#D96C4A]"
              data-testid={`linked-bank-unlink-${b.id}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}



function RecentActivityStrip() {
  const [events, setEvents] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/recent-activity?limit=5")
      .then((r) => { if (!cancelled) setEvents(r.data?.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, []);

  if (!events || events.length === 0) return null;

  const relTime = (iso) => {
    if (!iso) return "";
    const then = new Date(iso);
    const diff = (Date.now() - then.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <div
      className="rounded-2xl border border-[#E5E0D8] bg-gradient-to-br from-[#FBF9F6] to-white p-4 md:p-5"
      data-testid="dashboard-recent-activity"
    >
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-3 flex items-center gap-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#184A30] animate-pulse" />
        Pulse — recent activity
      </p>
      <ul className="divide-y divide-[#E5E0D8]">
        {events.map((e, i) => (
          <li
            key={`${e.kind}-${e.ts}-${i}`}
            onClick={() => e.link && nav(e.link)}
            className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-[#F8F4ED] rounded-md px-2 -mx-2 transition"
            data-testid={`activity-${e.kind}-${i}`}
          >
            <span className="text-lg leading-none flex-shrink-0">{e.icon}</span>
            <p className="text-sm text-[#112318] flex-1 min-w-0 truncate">{e.text}</p>
            <span className="text-[11px] text-[#4A5C50] flex-shrink-0 mono">{relTime(e.ts)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ThankYouFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cards/thank-you-feed")
      .then((r) => setItems(r.data?.thank_yous || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <div className="mb-10 rounded-2xl border border-[#D96C4A]/30 bg-white p-6" data-testid="dashboard-thankyou-feed">
      <div className="flex items-center gap-2 mb-4">
        <Heart size={20} weight="duotone" className="text-[#D96C4A]" />
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Thank-yous you've received</p>
      </div>
      <ul className="space-y-3">
        {items.slice(0, 5).map((t) => (
          <li
            key={t.id}
            className="flex items-start gap-3 pb-3 border-b last:border-0 border-[#E5E0D8]"
            data-testid={`thankyou-row-${t.id.slice(0, 8)}`}
          >
            <span className="text-2xl shrink-0 leading-none mt-0.5">{t.emoji || "🙏"}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#112318]">
                <b>{t.receiver_name}</b>
                <span className="text-[#4A5C50]"> · ${Number(t.amount_usd).toFixed(2)} at </span>
                <span className="text-[#112318]">{t.merchant_name}</span>
              </p>
              {t.message && (
                <p className="text-sm text-[#4A5C50] italic mt-0.5 break-words">"{t.message}"</p>
              )}
              <p className="text-[10px] mono text-[#4A5C50] mt-0.5">
                {new Date(t.ts).toLocaleString()}
              </p>
            </div>
          </li>
        ))}
      </ul>
      {items.length > 5 && (
        <p className="text-[11px] text-[#4A5C50] mt-2">
          +{items.length - 5} earlier · only the latest 25 are kept here.
        </p>
      )}
    </div>
  );
}


function ReferralLinkPanel() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referrals").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data?.referral_code) return null;

  const url = `${window.location.origin}/r/${data.referral_code}`;
  const stats = data.stats || { referrals_count: 0, active_funded_total_usd: 0 };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy");
    }
  };
  const share = () => {
    if (navigator.share) {
      navigator.share({
        title: "Try Zcardon",
        text: "I use Zcardon to send virtual debit cards. Sign up via my link:",
        url,
      }).catch(() => {});
    } else {
      copy();
    }
  };

  return (
    <div
      className="mb-10 rounded-2xl border border-[#184A30]/20 bg-white p-6"
      data-testid="dashboard-referral-panel"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Share size={18} weight="duotone" className="text-[#184A30]" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Your share link</p>
          </div>
          <div
            className="mt-2 break-all text-[12px] mono text-[#4A5C50] bg-[#FBF9F6] rounded-lg p-2 border border-[#E5E0D8]"
            data-testid="referral-share-url"
          >
            {url}
          </div>
          <p className="text-xs text-[#4A5C50] mt-2">
            Friends who sign up through this link show up below — and their card volume rolls up to your total.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 shrink-0 self-stretch">
          <Button
            onClick={copy}
            variant="outline"
            className="rounded-xl h-10 px-4"
            data-testid="referral-copy-btn"
          >
            <Copy size={14} className="mr-1" /> {copied ? "✓ Copied" : "Copy"}
          </Button>
          <Button
            onClick={share}
            className="rounded-xl h-10 px-4 bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="referral-share-btn"
          >
            <Share size={14} className="mr-1" /> Share
          </Button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div
          className="rounded-xl border border-[#E5E0D8] bg-[#FBF9F6] p-4 text-center"
          data-testid="referral-stat-count"
        >
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">People referred</p>
          <p className="serif text-3xl text-[#184A30] mt-1">{stats.referrals_count}</p>
        </div>
        <div
          className="rounded-xl border border-[#E5E0D8] bg-[#FBF9F6] p-4 text-center"
          data-testid="referral-stat-funded"
        >
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Active funded total</p>
          <p className="serif text-3xl text-[#184A30] mt-1">${Number(stats.active_funded_total_usd).toFixed(2)}</p>
        </div>
      </div>
      {(data.referrals || []).length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-[#E5E0D8] pt-4">
          {data.referrals.slice(0, 5).map((r) => (
            <li
              key={r.id}
              className="flex items-baseline gap-2 text-sm"
              data-testid={`referral-row-${r.id.slice(0, 8)}`}
            >
              <span className="text-[#112318] truncate">{r.referred_email}</span>
              <span className="text-[10px] mono text-[#4A5C50] ml-auto shrink-0">
                {r.cards_funded_count > 0
                  ? `${r.cards_funded_count} card${r.cards_funded_count === 1 ? "" : "s"} · $${r.funded_usd.toFixed(2)}`
                  : "no cards yet"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function IssueTrustPill() {
  // Inline social proof at the most anxious moment — typing a stranger's
  // email. Pulls live numbers from /api/public/trust-badges (already gated to
  // a meaningful sample size). Renders nothing when data is absent.
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/public/trust-badges")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data?.enough_data || data.confirmation_rate_pct === null) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-xl border border-[#184A30]/20 bg-[#184A30]/5 px-3 py-2 text-xs text-[#184A30]"
      data-testid="issue-trust-pill"
    >
      <ShieldCheck size={14} weight="duotone" />
      <span>
        <b>{data.confirmation_rate_pct}%</b> of recipients confirm receipt within 24h
        <span className="text-[#4A5C50]"> · from {data.decided_count} responses last 30 days</span>
      </span>
    </div>
  );
}


function MaintenanceBanner() {
  // Sticky maintenance notice for logged-in customers — pulled from the
  // master /admin/maintenance-mode setting via the public endpoint. Renders
  // nothing when not active. Auto-refreshes every 90s so customers see the
  // banner clear without a page reload.
  const [data, setData] = useState({ active: false, banner: null });
  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/public/maintenance-banner")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 90000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  if (!data?.active) return null;
  return (
    <div
      className="mb-10 rounded-2xl border-2 border-[#D96C4A]/40 bg-[#D96C4A]/10 p-5"
      data-testid="dashboard-maintenance-banner"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <WarningIcon size={22} weight="duotone" className="text-[#B04E2F] shrink-0 mt-0.5" />
        <div className="text-sm text-[#B04E2F]">
          <p className="font-semibold">Maintenance window in progress</p>
          <p className="mt-1 text-[#4A5C50]" data-testid="dashboard-maintenance-banner-text">
            {data.banner || "We're making improvements right now. Some actions may be temporarily paused."}
          </p>
        </div>
      </div>
    </div>
  );
}
